import {
  type AppliedRepair,
  type MessageCatalog,
  type Params,
  type RepairRuleId,
  renderMessage,
} from '@jsonfmt/core';

import type { RepairChip, RepairEntry } from './types';

/**
 * `trailing comma ×3 · comment ×1` — ONE spelling, shared by the two features that repair.
 *
 * ═══ WHY THIS IS NOT IN `beautify/` ═══
 *
 * It was, and two features needed it. `beautify.ts` builds this sentence for the
 * repair-then-format path (docs/ARCHITECTURE.md §5.3) and `editor/EditorPanel.tsx` needs the
 * same sentence for a document repaired on PASTE (docs/DESIGN.md §6.5) — but a feature may not
 * deep-import a neighbouring feature, and `@/features/beautify/beautify` is exactly that
 * (gate 2, ADR-0003). The choice was therefore between duplicating fifteen lines into the
 * editor and hoisting them out of both.
 *
 * Duplicating loses the property the sentence exists for. DESIGN §6.5's contract is that the
 * user is told EXACTLY what changed — not "the JSON was repaired" — and two copies of a
 * grouping rule drift the first time one of them learns about a new rule id. The legacy build
 * lost this argument once already, in the other direction, by appending an English suffix to a
 * translated string.
 *
 * ═══ WHY HERE, AND WHY IT IS NOT A LAYERING HOLE ═══
 *
 * `src/features/repairSummary.ts` sits at the ROOT of `features/`, beside `types.ts` and
 * `registry.ts`, which is a tier both features already depend on. Gate 2's neighbour rule
 * denies a TWO-segment specifier — a path INTO another feature's folder, such as
 * `@/features/beautify/beautify` — while this module is a one-segment specifier at the shared
 * root, so the rule is satisfied as written and, more importantly, as meant: nothing here knows
 * that `beautify` or `editor` exist, and it cannot grow to, because it takes a translator and a
 * catalog and returns a string.
 *
 * It is compiled by `tsconfig.core.json` like every other pure `.ts` under `features/`, so it
 * is DOM-free by construction.
 */

/**
 * The translator, widened. Both callers already hold one of these — `CommandContext.t` and
 * `FeatureApi.t` are declared with exactly this signature in `features/types.ts`.
 */
export type TranslateFn = (key: string, params?: Params) => string;

/**
 * Grouped by rule and ordered by FIRST APPLICATION, so the sentence reads in the order the
 * engine actually worked rather than alphabetically or by frequency.
 *
 * `editor.repair.chip` is the same template the repair banner's chips use — one spelling of
 * "label ×count" in the product, not two — and the labels come from the engine's own
 * `fix.<rule-id>` catalog, so they are translated by whatever locale is loaded and cannot
 * drift from the rule that produced them.
 */
export function summariseRepairs(
  t: TranslateFn,
  applied: readonly AppliedRepair[],
  catalog: MessageCatalog,
): string {
  return groupRepairs(applied, catalog)
    .map((entry) => t('editor.repair.chip', { label: entry.label, count: entry.count }))
    .join(' · ');
}

/**
 * The same grouping, unrendered: `[{ label: 'Remove the trailing comma', count: 3 }, …]`.
 *
 * The sentence above and the CHIP ROW of the repair banner (docs/DESIGN.md §6.5) are two
 * presentations of one fact, and §6.5 states as a code-level invariant that the banner's
 * headline count equals the sum of the chips. That invariant is only checkable if both come
 * from the same list, so the list is what this returns and `summariseRepairs` is the string
 * view of it. `src/app/` builds `Notice.repair.chips` from here.
 */
export function groupRepairs(
  applied: readonly AppliedRepair[],
  catalog: MessageCatalog,
): readonly RepairChip[] {
  const counts = new Map<RepairRuleId, { readonly label: string; count: number }>();

  for (const repair of applied) {
    const existing = counts.get(repair.ruleId);
    if (existing !== undefined) {
      existing.count += 1;
      continue;
    }
    counts.set(repair.ruleId, {
      label: renderMessage(repair.labelCode, catalog, repair.labelParams),
      count: 1,
    });
  }

  return [...counts.values()].map((entry) => ({ label: entry.label, count: entry.count }));
}

/**
 * The same applied repairs, UNGROUPED and with their bytes — the rows of the Repair log
 * (ADR-0101 §7.15).
 *
 * ═══ WHY THIS IS NOT `groupRepairs` WITH A FLAG ═══
 *
 * The two answer different questions and the shapes say so. `groupRepairs` answers "what kinds
 * of thing did you change, and how many of each", which is what a one-line banner can honestly
 * claim; this answers "show me each one", which is the only form in which a user can CHECK the
 * claim. Folding them together would mean a function whose return type depends on an argument,
 * and every caller would then branch on the same flag it just passed in.
 *
 * ═══ THE ORDER IS THE ENGINE'S ═══
 *
 * `applied` arrives in application order across passes (`core/repair/engine.ts`), and it is
 * kept: the log reads in the order the repair actually worked, so a row's `line:column`, which
 * describes the text as it was before ITS pass, is read in the same frame of reference it was
 * recorded in. Sorting by position would silently mix coordinates from two different texts.
 *
 * Nothing is truncated here. A 400-repair paste produces 400 rows and the drawer scrolls;
 * deciding that "the first twenty are enough" is a presentation choice, and this module is the
 * one that must not make it — `RepairLog.tsx` can cap what it draws, this cannot un-drop a row.
 */
export function listRepairs(
  applied: readonly AppliedRepair[],
  catalog: MessageCatalog,
): readonly RepairEntry[] {
  return applied.map((repair) => ({
    label: renderMessage(repair.labelCode, catalog, repair.labelParams),
    before: repair.before,
    after: repair.after,
    line: repair.at.line,
    column: repair.at.column,
  }));
}
